import { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { sendEmail, getSubscriber } from '../../api/email';
import bgBtn from '../../assets/images/btn-get-em.png';
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useStyles } from './styles';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { getPodcastIdFromUrl } from '../../utils/updatePodcastUrl';

export const EmailNotify = () => {
  const classes = useStyles({ bgBtn });
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [frequency, setFrequency] = useState('daily');
  const [anchorEl, setAnchorEl] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [message, setMessage] = useState('');
  const open = Boolean(anchorEl);
  const { currentPodcast, browseMode, selectedCountry } = useSelector((state) => state.podcasts);

  // Email validation schema
  const validationSchema = Yup.object({
    email: Yup.string().email('Please enter a valid email address').required('Email is required'),
  });

  useEffect(() => {
    let timer;
    if (showSuccess) {
      timer = setTimeout(() => {
        setMessage('');
        setShowSuccess(false);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccess]);

  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError }) => {
    if (!values.email.trim()) {
      setFieldError('email', 'Email is required');
      setSubmitting(false);
      return;
    }

    setIsLoading(true);
    setSubmittedEmail(values.email);

    try {
      // Check if user is already subscribed to Podnews
      const subscriberResponse = await getSubscriber(values.email);
      if (subscriberResponse?.isSubscribedToPodnews !== undefined) {
        // User is already subscribed, send email directly
        const podcastId = getPodcastIdFromUrl();
        const response = await sendEmail({
          email: values.email,
          podcastId,
          ...(frequency === 'weekly' ? { dayOfWeek: moment().format('dd') } : {}),
          ...(selectedCountry ? { country: selectedCountry } : {}),
        });
        setMessage(response.message);
        resetForm();
        setShowSuccess(true);
      } else {
        // User is not subscribed, show dialog to ask if they want to subscribe
        setOpenDialog(true);
      }
    } catch (error) {
      console.log('error :>> ', error);
      setFieldError(
        'email',
        error.response?.data?.message || 'Failed to process request. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = async (subscribe, { setFieldError, resetForm }) => {
    setOpenDialog(false);
    if (submittedEmail) {
      try {
        const podcastId = getPodcastIdFromUrl();
        // Here you would implement the actual Podnews subscription logic
        const response = await sendEmail({
          email: submittedEmail,
          podcastId,
          ...(frequency === 'weekly' ? { dayOfWeek: moment().format('dd') } : {}),
          ...(subscribe ? { subscribeToPodnews: true } : {}),
          ...(selectedCountry ? { country: selectedCountry } : {}),
        });
        setMessage(response.message);
        resetForm();
        setShowSuccess(true);
      } catch (error) {
        setFieldError(
          'email',
          error.response.data.message || 'Failed to send email. Please try again.'
        );
      } finally {
        setIsLoading(false);
      }
    }
    setShowSuccess(true);
    setSubmittedEmail('');
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (value) => {
    setAnchorEl(null);
    if (value) {
      setFrequency(value);
    }
  };

  return (
    <Box className={classes.notifyWrapper}>
      <Formik
        initialValues={{ email: '' }}
        validationSchema={validationSchema}
        validateOnBlur={false}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, errors, touched, ...rest }) => (
          <Form>
            <Box className={classes.notifyForm} sx={{ position: 'relative' }}>
              <Typography variant="h7" component="h2" className={classes.notifyTitle}>
                Get
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    mx: 0.5,
                    color: !currentPodcast || browseMode ? 'inherit' : '#4dabf5',
                    textDecoration: 'underline',
                    cursor: !currentPodcast || browseMode ? 'default' : 'pointer',
                  }}
                  onClick={!currentPodcast || browseMode ? undefined : handleMenuClick}
                >
                  {frequency}
                </Box>
                <Menu anchorEl={anchorEl} open={open} onClose={() => handleMenuClose()}>
                  <MenuItem onClick={() => handleMenuClose('daily')}>daily</MenuItem>
                  <MenuItem onClick={() => handleMenuClose('weekly')}>weekly</MenuItem>
                </Menu>
                notifications for this podcast
              </Typography>
              <Box className={classes.notifyControls}>
                <Field
                  name="email"
                  type="email"
                  placeholder="Your email"
                  disabled={!currentPodcast || browseMode || isLoading}
                  className={classes.notifyInput}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                />
                {errors.email && (
                  <Typography
                    sx={{
                      color: '#f44336',
                      fontSize: '0.75rem',
                      marginTop: '4px',
                      marginLeft: '8px',
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {errors.email}
                  </Typography>
                )}
                {showSuccess && (
                  <Typography
                    sx={{
                      color: 'red',
                      fontSize: '0.75rem',
                      position: 'absolute',
                      top: '80px',
                      opacity: 1,
                      fontWeight: 600,
                      transition: 'opacity 0.3s ease-in-out',
                      padding: '8px 16px',
                      zIndex: 1,
                    }}
                  >
                    {message}
                  </Typography>
                )}
                <Button
                  type="submit"
                  disabled={isLoading || !currentPodcast}
                  className={classes.notifyButton}
                >
                  {isLoading ? <CircularProgress size={24} color="inherit" /> : ''}
                </Button>
              </Box>
            </Box>
            <Dialog
              open={openDialog}
              onClose={() => handleDialogClose(false, rest)}
              aria-labelledby="podnews-dialog-title"
              PaperProps={{
                sx: {
                  backgroundColor: '#1d1d1c',
                  borderRadius: 2,
                  p: 1,
                  color: '#fff',
                },
              }}
            >
              <DialogTitle id="podnews-dialog-title">Subscribe to Podnews</DialogTitle>
              <DialogContent>
                <Typography>
                  Would you also like to subscribe to Podnews and get daily news on the podcast
                  industry?
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => handleDialogClose(false, rest)}
                  variant="outlined"
                  sx={{ minWidth: 100 }}
                >
                  No
                </Button>
                <Button
                  onClick={() => handleDialogClose(true, rest)}
                  variant="contained"
                  sx={{ minWidth: 100 }}
                >
                  Yes
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
